/**
 * AdminProduction — 工厂生产看板
 * 切换到"包装待发"时联动：
 *   - orders.order_status → 'ready'
 *   - logistics 表自动插入 pending 记录（carrier 默认'待填写'）
 * 切换其他在产阶段：仅从 pending 推进为 producing
 */
import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  RefreshCw, Search, AlertCircle, ChevronDown, ChevronUp,
  Clock, Package2, CheckCircle2, Loader2, Wrench, FlaskConical,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const ORANGE = 'oklch(0.66 0.19 42)';
const CARD_BG = '#FFFFFF';
const ROW_HOVER = 'rgba(26,35,55,0.04)';

type Stage = 'scheduling' | 'raw_material' | 'vulcanizing' | 'qc_passed' | 'packing';

interface ProductionTask {
  id: string;
  order_id: string;
  stage: Stage;
  tech_notes: string | null;
  assigned_to: string | null;
  created_at: string;
  updated_at: string;
  order_no?: string;
  materials_summary?: string;
}

const STAGE_CFG: Record<Stage, { label: string; color: string; bg: string; icon: React.ElementType; step: number }> = {
  scheduling:   { label: '排产中',     color: 'text-indigo-600', bg: 'bg-indigo-100 border-indigo-300', icon: Clock,        step: 1 },
  raw_material: { label: '原材料准备', color: 'text-yellow-700', bg: 'bg-yellow-100 border-yellow-300', icon: Package2,     step: 2 },
  vulcanizing:  { label: '硫化/切割中',color: 'text-orange-600', bg: 'bg-orange-100 border-orange-300', icon: Wrench,       step: 3 },
  qc_passed:    { label: '质检完成',   color: 'text-cyan-700',   bg: 'bg-cyan-100 border-cyan-300',     icon: FlaskConical, step: 4 },
  packing:      { label: '包装待发',   color: 'text-amber-700',  bg: 'bg-amber-100 border-amber-300',   icon: CheckCircle2, step: 5 },
};

const STAGES = Object.keys(STAGE_CFG) as Stage[];

function StageProgress({ current }: { current: Stage }) {
  const currentStep = STAGE_CFG[current].step;
  return (
    <div className="flex items-center gap-1 mt-2">
      {STAGES.map((s, i) => {
        const step = i + 1;
        const done = step < currentStep;
        const active = step === currentStep;
        return (
          <div key={s} className="flex items-center gap-1">
            <div className={`w-2 h-2 rounded-full transition-all ${active ? 'scale-150' : ''}`}
              style={{ background: done || active ? ORANGE : '#D0D5E2' }} />
            {i < STAGES.length - 1 && (
              <div className="w-4 h-0.5" style={{ background: done ? ORANGE : '#D0D5E2' }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export function ProductionView() {
  const [tasks, setTasks] = useState<ProductionTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [editNotes, setEditNotes] = useState<Record<string, string>>({});

  const fetchTasks = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true); else setLoading(true);

    const { data: tasksData } = await supabase
      .from('production_tasks')
      .select('*, orders(id, order_no)')
      .order('created_at', { ascending: false });

    if (!tasksData) { setLoading(false); setRefreshing(false); return; }

    const orderIds = [...new Set(
      tasksData
        .map((t: Record<string, unknown>) => (t.orders as Record<string, unknown> | null)?.id as string)
        .filter(Boolean)
    )];
    const itemsMap: Record<string, string> = {};

    if (orderIds.length > 0) {
      const { data: itemsData } = await supabase
        .from('order_items')
        .select('order_id, material_code, specifications, quantity_kg')
        .in('order_id', orderIds);

      if (itemsData) {
        (itemsData as Record<string, unknown>[]).forEach((item) => {
          const oid = item.order_id as string;
          const desc = `${item.material_code || ''}${item.specifications ? ' ' + item.specifications : ''}${item.quantity_kg ? ' · ' + item.quantity_kg + 'kg' : ''}`;
          itemsMap[oid] = itemsMap[oid] ? itemsMap[oid] + ' | ' + desc : desc;
        });
      }
    }

    setTasks(tasksData.map((t: Record<string, unknown>) => {
      const order = t.orders as Record<string, unknown> | null;
      const orderId = order?.id as string | undefined;
      return {
        ...t,
        order_no: order?.order_no as string | undefined,
        materials_summary: orderId ? itemsMap[orderId] : undefined,
      } as ProductionTask;
    }));

    setLoading(false); setRefreshing(false);
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  // 顺序推进：只允许推进到下一个阶段
  const advanceStage = async (taskId: string) => {
    setSavingId(taskId);
    const task = tasks.find(t => t.id === taskId);
    if (!task) { setSavingId(null); return; }

    const currentStep = STAGE_CFG[task.stage].step;
    const nextStage = STAGES.find(s => STAGE_CFG[s].step === currentStep + 1);
    if (!nextStage) { setSavingId(null); return; }

    // 1. 更新生产任务阶段
    await supabase.from('production_tasks').update({ stage: nextStage }).eq('id', taskId);

    // 2. 联动订单+线索状态
    if (task.order_id) {
      // 同步订单状态
      await supabase.from('orders').update({ order_status: nextStage }).eq('id', task.order_id);
      // 同步关联线索状态
      const { data: ord } = await supabase.from('orders').select('inquiry_id').eq('id', task.order_id).single();
      if (ord?.inquiry_id) {
        await supabase.from('inquiries').update({ status: nextStage }).eq('id', ord.inquiry_id);
      }

      if (nextStage === 'packing') {
        // 包装待发 → 自动在 logistics 表插一条 pending 记录（幂等）
        const { data: existing } = await supabase.from('logistics').select('id')
          .eq('order_id', task.order_id).eq('status', 'pending').maybeSingle();
        if (!existing) {
          await supabase.from('logistics').insert([{ order_id: task.order_id, status: 'pending', carrier: '待填写' }]);
        }
      }
    }

    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, stage: nextStage } : t));
    setSavingId(null);
  };

  const saveNotes = async (id: string) => {
    setSavingId(id);
    await supabase.from('production_tasks').update({ tech_notes: editNotes[id] }).eq('id', id);
    setTasks(prev => prev.map(t => t.id === id ? { ...t, tech_notes: editNotes[id] } : t));
    setSavingId(null);
  };

  const filtered = tasks.filter(t => !search ||
    [t.order_no ?? '', t.materials_summary ?? '', t.tech_notes ?? ''].some(v =>
      v.toLowerCase().includes(search.toLowerCase())
    )
  );

  const stageCounts = STAGES.reduce(
    (acc, s) => ({ ...acc, [s]: tasks.filter(t => t.stage === s).length }),
    {} as Record<Stage, number>
  );

  return (
    <div className="space-y-6">
      {/* 生产进度总览 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {STAGES.map(s => {
          const cfg = STAGE_CFG[s];
          const Icon = cfg.icon;
          return (
            <div key={s} className="rounded-sm border border-border p-3" style={{ background: CARD_BG }}>
              <div className="flex items-center gap-1.5 mb-1">
                <Icon className={`w-3.5 h-3.5 ${cfg.color}`} />
                <span className="text-[10px] font-mono" style={{ color: '#5A6780' }}>{cfg.label}</span>
              </div>
              <div className="text-2xl font-black" style={{ color: stageCounts[s] > 0 ? ORANGE : '#3A4A5C' }}>
                {stageCounts[s]}
              </div>
            </div>
          );
        })}
      </div>

      {/* 搜索 + 刷新 */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: '#5A6780' }} />
          <input type="search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="搜索订单号、材料…"
            className="w-full pl-9 pr-4 py-2 rounded-sm text-sm outline-none"
            style={{ background: '#FFFFFF', border: '1px solid #D0D5E2', color: '#1A2337' }}
          />
        </div>
        <button onClick={() => fetchTasks(true)} disabled={refreshing}
          className="p-2 rounded-sm border border-border disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} style={{ color: '#5A6780' }} />
        </button>
      </div>

      {/* 任务列表 */}
      {loading ? (
        <div className="p-16 flex justify-center">
          <RefreshCw className="w-8 h-8 animate-spin" style={{ color: ORANGE }} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="p-16 flex flex-col items-center gap-3 rounded-sm border border-border"
          style={{ background: CARD_BG, color: '#5A6780' }}>
          <AlertCircle className="w-10 h-10" /><span>暂无生产任务</span>
        </div>
      ) : (
        <div className="space-y-3">
          <AnimatePresence>
            {filtered.map(task => {
              const cfg = STAGE_CFG[task.stage];
              const Icon = cfg.icon;
              const isExpanded = expandedId === task.id;
              return (
                <motion.div key={task.id} layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className="rounded-sm border border-border overflow-hidden" style={{ background: CARD_BG }}>
                  {/* 任务头 */}
                  <div className="flex items-center gap-4 px-5 py-4 cursor-pointer"
                    onClick={() => setExpandedId(isExpanded ? null : task.id)}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = ROW_HOVER; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-sm font-bold font-mono" style={{ color: ORANGE }}>{task.order_no ?? '—'}</span>
                        {task.materials_summary && (
                          <span className="text-xs max-w-xs truncate" style={{ color: '#5A6780' }}>{task.materials_summary}</span>
                        )}
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-sm border ${cfg.bg} ${cfg.color}`}>
                          <Icon className="w-3 h-3" />{cfg.label}
                        </span>
                      </div>
                      <StageProgress current={task.stage} />
                    </div>
                    <div className="flex items-center gap-2" style={{ color: '#5A6780' }}>
                      <span className="text-xs hidden sm:block">{new Date(task.updated_at).toLocaleDateString('zh-CN')}</span>
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </div>

                  {/* 展开详情 */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }} className="border-t border-border px-5 py-5"
                        style={{ background: '#F7F8FA' }}>
                        <div className="grid md:grid-cols-2 gap-6">
                          {/* 更新生产节点 — 顺序锁定 */}
                          <div className="space-y-3">
                            <p className="text-xs font-bold tracking-widest uppercase" style={{ color: '#5A6780' }}>生产节点进度</p>
                            <div className="space-y-2">
                              {STAGES.map(s => {
                                const scfg = STAGE_CFG[s];
                                const SIcon = scfg.icon;
                                const currentStep = STAGE_CFG[task.stage].step;
                                const thisStep = scfg.step;
                                const done = thisStep < currentStep;
                                const active = task.stage === s;
                                const isNext = thisStep === currentStep + 1;
                                return (
                                  <div key={s} className="flex items-center gap-3 px-3 py-2 rounded-sm border"
                                    style={{
                                      background: active ? `${ORANGE}12` : done ? '#F0FDF4' : '#FAFAFA',
                                      borderColor: active ? ORANGE : done ? '#86EFAC' : '#E5E7EB',
                                    }}>
                                    {done
                                      ? <CheckCircle2 className="w-4 h-4 text-green-500" />
                                      : <SIcon className={`w-4 h-4 ${active ? '' : 'text-gray-300'}`} style={active ? { color: ORANGE } : {}} />
                                    }
                                    <span className="text-xs font-medium" style={{ color: done ? '#16A34A' : active ? ORANGE : '#9CA3AF' }}>
                                      {scfg.step}. {scfg.label}
                                    </span>
                                    {active && <span className="ml-auto text-[10px] font-bold" style={{ color: ORANGE }}>当前节点</span>}
                                    {isNext && (
                                      <button onClick={() => advanceStage(task.id)} disabled={savingId === task.id}
                                        className="ml-auto flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-sm disabled:opacity-60"
                                        style={{ background: ORANGE, color: '#fff' }}>
                                        {savingId === task.id ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                                        推进 →
                                      </button>
                                    )}
                                    {thisStep > currentStep + 1 && (
                                      <span className="ml-auto text-[10px]" style={{ color: '#D1D5DB' }}>🔒 锁定</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          {/* 技术备注 */}
                          <div className="space-y-3">
                            <p className="text-xs font-bold tracking-widest uppercase" style={{ color: '#5A6780' }}>打样与技术备注</p>
                            <textarea rows={6}
                              value={editNotes[task.id] ?? task.tech_notes ?? ''}
                              onChange={e => setEditNotes(prev => ({ ...prev, [task.id]: e.target.value }))}
                              placeholder="记录配方调整、打样结果、技术要点…"
                              className="w-full rounded-sm text-sm outline-none resize-none"
                              style={{ background: '#FFFFFF', border: '1px solid #D0D5E2', color: '#1A2337', padding: '10px 12px' }} />
                            <button onClick={() => saveNotes(task.id)} disabled={savingId === task.id}
                              className="flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-sm disabled:opacity-60"
                              style={{ background: ORANGE, color: '#fff' }}>
                              {savingId === task.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                              保存备注
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}